import React from 'react';
import {
  Box,
  MenuItem,
  Typography,
  Menu,
  ListItemIcon,
  ListItemText,
  Divider,
} from '@mui/material';
import LanguageIcon from '@mui/icons-material/Language';
import CheckIcon from '@mui/icons-material/Check';
import { useTranslation } from 'react-i18next';
import { supportedLanguages } from '../../i18n';

interface LanguageSelectorProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
}

const LanguageSelector: React.FC<LanguageSelectorProps> = ({
  anchorEl,
  open,
  onClose,
}) => {
  const { i18n, t } = useTranslation();

  const handleLanguageChange = (languageCode: string) => {
    i18n.changeLanguage(languageCode);
    onClose();
  };

  return (
    <Menu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{
        vertical: 'top',
        horizontal: 'left',
      }}
      transformOrigin={{
        vertical: 'top',
        horizontal: 'right',
      }}
      sx={{ ml: -2 }}
    >
      <Box sx={{ px: 2, py: 1, display: 'flex', alignItems: 'center' }}>
        <LanguageIcon sx={{ mr: 1, fontSize: '1.25rem', color: 'text.secondary' }} />
        <Typography variant="subtitle2" color="text.secondary">
          {t('navbar:userMenu.language')}
        </Typography>
      </Box>
      <Divider />
      {supportedLanguages.map((language) => (
        <MenuItem
          key={language.code}
          onClick={() => handleLanguageChange(language.code)}
          selected={i18n.language === language.code}
          data-testid={`language-option-${language.code}`}
        >
          <ListItemIcon sx={{ minWidth: 32 }}>
            <Typography sx={{ fontSize: '1.2rem' }}>{language.flag}</Typography>
          </ListItemIcon>
          <ListItemText primary={language.name} />
          {i18n.language === language.code && (
            <CheckIcon sx={{ ml: 1, fontSize: '1rem', color: 'primary.main' }} />
          )}
        </MenuItem>
      ))}
    </Menu>
  );
};

// Inline LanguageMenuItem für das Hauptmenü
interface LanguageMenuItemProps {
  onClick: (event: React.MouseEvent<HTMLElement>) => void;
}

export const LanguageMenuItem: React.FC<LanguageMenuItemProps> = ({ onClick }) => {
  const { i18n, t } = useTranslation();

  const currentLanguage = supportedLanguages.find(
    (lang) => lang.code === i18n.language
  ) || supportedLanguages[0];

  return (
    <MenuItem onClick={onClick} data-testid="navbar-language-menu-item">
      <Box sx={{ display: 'flex', alignItems: 'center', width: '100%' }}>
        <Typography sx={{ color: 'primary.dark', flex: 1 }}>
          {t('navbar:userMenu.language')}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <Typography sx={{ fontSize: '1rem' }}>{currentLanguage.flag}</Typography>
          <Typography sx={{ fontSize: '0.875rem', color: 'text.secondary' }}>
            {currentLanguage.code.toUpperCase()}
          </Typography>
        </Box>
      </Box>
    </MenuItem>
  );
};

export default LanguageSelector;

